import React, { useState } from 'react';
import i18next from 'i18next';
import { Facet } from '../SearchModels';
import FilterSelector from './FilterSelector';
import Panel from '../../../StyledComponents/Panel';
import { FooterAction, FooterActionItem } from '../../../StyledComponents/Panel/wrapper';
import { RepeatSmall } from '../../../StyledComponents/Repeat';
import {
  ButtonContent,
  ButtonPrimary,
  ButtonSecondary,
} from '../../../StyledComponents/Button/index';
import Accordion, { AccordionItem } from '../../../StyledComponents/Accordion';
import Loader from '../../../StyledComponents/Loader';
import QueryFilter from './QueryFilter';
import { isSelected } from '../Utils/Filter';
import Snackbar from '../../../StyledComponents/Snackbar';

const removeUrlSearchParam = (params: URLSearchParams | undefined, key: string, value: string) => {
  const next = new URLSearchParams(params || '');
  const copy = next.getAll(key);
  next.delete(key);
  copy.forEach((c) => {
    if (c !== value) {
      next.append(key, c);
    }
  });
  return next;
};

const addUrlSearchParam = (params: URLSearchParams | undefined, key: string, value: string) => {
  const next = new URLSearchParams(params || '');
  next.append(key, value);
  return next;
};

interface FilterPanelProps {
  facets?: Facet[];
  queryString?: string;
  loading?: boolean;
  initialFacets?: Facet[];
  urlSearchParams?: URLSearchParams;
  totalHitsText: string;
  query?: string;
  onCloseFilter: () => void;
  onFilterUpdate: (query: string) => void;
}

const FilterPanel = ({
  facets: _facets,
  queryString,
  loading,
  initialFacets,
  urlSearchParams,
  totalHitsText,
  query,
  onCloseFilter,
  onFilterUpdate,
}: FilterPanelProps) => {
  const facets = initialFacets?.map((f) => {
    const matching = _facets?.find((x) => x.id === f.id);

    return {
      ...f,
      displayName: matching?.displayName || f.displayName,
      filters: f.filters.map((y) => {
        const matchingFilter = matching?.filters.find((f) => f.displayName === y.displayName);
        const selected = matchingFilter?.selected || false;

        const _query = selected
          ? removeUrlSearchParam(urlSearchParams, f.id, y.displayName)
          : addUrlSearchParam(urlSearchParams, f.id, y.displayName);

        return {
          ...y,
          count: matchingFilter?.count || 0,
          selected,
          query: _query.toString(),
        };
      }),
    };
  });

  const [queryFilter] = useState(query);
  return (
    <Panel
      title={i18next.t('search.filter')}
      onClose={onCloseFilter}
      footer={
        <>
          <FooterAction>
            <FooterActionItem>
              <ButtonSecondary
                fullWidth
                disabled={loading}
                onClick={() => {
                  onFilterUpdate('');
                  onCloseFilter();
                }}
                title={i18next.t('search.clearAllFilters.title') || ''}
              >
                {i18next.t('search.clearAllFilters.text')}
              </ButtonSecondary>
            </FooterActionItem>
            <FooterActionItem>
              <ButtonPrimary fullWidth disabled={loading} onClick={onCloseFilter}>
                <ButtonContent>{totalHitsText}</ButtonContent>
                {loading && (
                  <ButtonContent>
                    <Loader inline />
                  </ButtonContent>
                )}
              </ButtonPrimary>
            </FooterActionItem>
          </FooterAction>
        </>
      }
    >
      {!facets?.length && query && (
        <RepeatSmall>
          <Snackbar
            severity="warning"
            icon
            heading={i18next.t('search.snackBarClearFreeText.heading') || undefined}
          >
            {i18next.t('search.snackBarClearFreeText.body')}
          </Snackbar>
        </RepeatSmall>
      )}
      <RepeatSmall>
        <Accordion>
          {queryFilter && (
            <AccordionItem heading={`${i18next.t('search.freeText')}${query ? ` (1)` : ''}`}>
              <QueryFilter
                query={queryFilter}
                selected={!!query}
                urlSearchParams={urlSearchParams}
                loading={loading}
                onFilterUpdate={onFilterUpdate}
              />
            </AccordionItem>
          )}
          {facets?.map((facet) => (
            <AccordionItem
              key={facet.id}
              heading={`${facet.displayName}${
                isSelected(facet, urlSearchParams) ? ` (${isSelected(facet, urlSearchParams)})` : ''
              }`}
            >
              <FilterSelector
                queryString={queryString}
                facet={facet}
                loading={loading}
                initialFacets={initialFacets}
                urlSearchParams={urlSearchParams}
                onFilterUpdate={onFilterUpdate}
              />
            </AccordionItem>
          ))}
        </Accordion>
      </RepeatSmall>
    </Panel>
  );
};

export default FilterPanel;
